import React, { FC, memo, useCallback, useEffect, useRef, useState } from 'react';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { StripeAddressElementChangeEvent } from '@stripe/stripe-js';
import { useDispatch } from 'react-redux';
import { Alert, Button, Typography } from '@mui/material';

import { CardWrapper } from '@monorepo/common';

import { ArrowRight as ArrowRightIcon } from '../../../Icons/arrow-right';
import { IEventsStepProps } from '../../interfaceSteps';
import { IStep7Form } from './formInterfaces';
import {
  JobPaymentElements,
  PaymentElementFields,
  confirmJobPayment,
  mapBillingToPayload,
} from '../../../JobPayment';
import { showNotification } from '../../../../features/sliceNotification';
import PaymentTitle from '../../components/PaymentTitle';
import { IJobBenefitClaim } from '../../../../services/servicePostAJob/responseInterfaces';
import { formatBenefitDeadline } from '../Step1/BenefitSelector';

interface IStep7Props extends IEventsStepProps {
  finalPaymentRequest: (dataForm: IStep7Form, validateOnly: boolean) => Promise<unknown>,
  validateFormData: (val: IStep7Form, validateOnly: boolean) => Promise<{ success: boolean, message: string }>,
  setIsInternalLoading: (value: boolean) => void,
  defaultData: IStep7Form,
  listingType: number,
  standardPrice: number | null,
  featuredPrice: number | null,
  // PaymentIntent is owned by the flow parent (JobStepper / ReactivateListing
  // StepperPanel) — see §Timing. It arrives here once the payment step is reached.
  clientSecret: string | null,
  intentError: string | null,
  retryIntent: () => void,
  benefitClaim?: IJobBenefitClaim | null,
  validateFreeFormData?: () => Promise<{ success: boolean, message: string }>,
  finalFreeRequest?: () => Promise<unknown>,
}

interface IStep7PaymentFormProps {
  defaultData: IStep7Form;
  clientSecret: string | null;
  payCtaText: string;
  onBack?: () => void;
  Nav: React.ElementType;
  finalPaymentRequest: (dataForm: IStep7Form, validateOnly: boolean) => Promise<unknown>;
  validateFormData: (val: IStep7Form, validateOnly: boolean) => Promise<{ success: boolean, message: string }>;
  setIsInternalLoading: (value: boolean) => void;
}

/**
 * Renders INSIDE <Elements> (so the Stripe hooks resolve). Owns the submit
 * sequence: map billing → server validateOnly gate → confirm (skipped when a PI
 * id is already memoized for THIS secret) → finalize with the PI id.
 */
const Step7PaymentForm: FC<IStep7PaymentFormProps> = ({
  defaultData,
  clientSecret,
  payCtaText,
  onBack,
  Nav,
  finalPaymentRequest,
  validateFormData,
  setIsInternalLoading,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [addressError, setAddressError] = useState<string | null>(null);
  const [email, setEmail] = useState<string>(defaultData?.billingEmail || '');
  const [company, setCompany] = useState<string>(defaultData?.billingCompany || '');

  const addressRef = useRef<{ complete: boolean; value: StripeAddressElementChangeEvent['value'] | null }>({
    complete: false,
    value: null,
  });
  // BillingEmail is NotEmpty+EmailAddress on the server, but it comes from Stripe's
  // Link element — there is no react-hook-form field here to hang a rule on. Track
  // the element's own completeness (same shape as addressRef) and gate submit on it,
  // so an empty/invalid email is caught here instead of as a Step 7 error blob after
  // a round trip. Seeded true when we prefill a saved email, since Link fires no
  // change event for a default value.
  const emailCompleteRef = useRef<boolean>(Boolean(defaultData?.billingEmail));
  // Confirmed PI id, keyed to the clientSecret it was confirmed under. If the
  // parent re-prices and mints a new secret, this whole subtree remounts (Elements
  // key={clientSecret}); the forSecret guard is the belt-and-suspenders that
  // guarantees we never submit a stale PI id against a fresh secret.
  const confirmedRef = useRef<{ piId: string; forSecret: string } | null>(null);
  // Single-flight guard: held for the whole submit (set synchronously before the
  // first await, released in `finally`). Prevents a second finalize — which the
  // server recovers as the same PaymentIntent and would turn into a duplicate paid
  // order. The ref alone is insufficient: finalize must also be AWAITED (below).
  const finalizeInFlightRef = useRef<boolean>(false);

  useEffect(() => {
    setIsInternalLoading(isLoading);
  }, [setIsInternalLoading, isLoading]);

  const onSubmit = useCallback(async () => {
    if (!clientSecret) return;
    // Single-flight: return immediately if a submit is already in flight. Set the
    // ref BEFORE the first await so a synchronous double-submit collapses to one.
    if (finalizeInFlightRef.current) return;
    finalizeInFlightRef.current = true;
    setIsLoading(true);
    setAddressError(null);

    try {
      // 1. Map billing — an incomplete address or email blocks submit (no money moved).
      const mapped = mapBillingToPayload({ email, company, address: addressRef.current });
      if (!mapped.ok) {
        setAddressError('Please complete your billing address.');
        return;
      }
      if (!emailCompleteRef.current) {
        setAddressError('Please enter a valid billing email address.');
        return;
      }
      const billing = mapped.prefixed;

      // 2. Server validateOnly gate (UNCHANGED) — must pass before any charge.
      const validation = await validateFormData({ ...billing }, true);
      if (!validation.success) {
        dispatch(showNotification({ show: false, type: 'error', text: validation.message }));
        return;
      }

      // 3. Confirm — reuse the memoized PI id when it matches the current secret
      //    (finalize-only retry after a card decline / network failure).
      let paymentIntentId = confirmedRef.current && confirmedRef.current.forSecret === clientSecret
        ? confirmedRef.current.piId
        : null;

      if (!paymentIntentId) {
        const confirmResult = await confirmJobPayment({ stripe, elements, clientSecret });
        if (confirmResult.error || !confirmResult.paymentIntentId) {
          dispatch(showNotification({
            show: false,
            type: 'error',
            text: confirmResult.error || 'Payment could not be completed. Please try again.',
          }));
          return;
        }
        paymentIntentId = confirmResult.paymentIntentId;
        confirmedRef.current = { piId: paymentIntentId, forSecret: clientSecret };
      }

      // 4. Finalize (UNCHANGED endpoint/payload — stripePaymentMethodId carries the PI
      //    id). AWAIT the RTK trigger so both the busy overlay and the single-flight
      //    guard stay held until the mutation SETTLES, not merely until dispatch.
      await finalPaymentRequest({ ...billing, stripePaymentMethodId: paymentIntentId }, false);
    } finally {
      finalizeInFlightRef.current = false;
      setIsLoading(false);
    }
  }, [clientSecret, email, company, validateFormData, finalPaymentRequest, dispatch, stripe, elements]);

  return (
    <form
      data-cy="step7"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <PaymentElementFields
        showCompany
        defaultEmail={defaultData?.billingEmail}
        company={company}
        onEmailChange={(value, complete) => {
          emailCompleteRef.current = complete;
          setEmail(value);
          if (complete && addressError) setAddressError(null);
        }}
        onCompanyChange={setCompany}
        onAddressChange={(evt) => {
          addressRef.current = { complete: evt.complete, value: evt.value };
          if (evt.complete && addressError) setAddressError(null);
        }}
      />

      {addressError && (
        <Typography
          variant="body2"
          color="error"
          sx={{ mt: 1 }}
        >
          {addressError}
        </Typography>
      )}

      <Nav
        nextDisabled
        onBack={onBack}
        textPreviousStep="← Back"
        nextButton={(
          <Button
            endIcon={(<ArrowRightIcon fontSize="small" />)}
            variant="contained"
            disabled={!stripe || isLoading}
            type="submit"
            data-cy="complete"
          >
            {payCtaText}
          </Button>
        )}
      />
    </form>
  );
};

interface IFreeReviewProps {
  claim: IJobBenefitClaim;
  isFeatured: boolean;
  onBack?: () => void;
  Nav: React.ElementType;
  validateFreeFormData: () => Promise<{ success: boolean; message: string }>;
  finalFreeRequest: () => Promise<unknown>;
  setIsInternalLoading: (value: boolean) => void;
}

const FreeReview: FC<IFreeReviewProps> = ({
  claim,
  isFeatured,
  onBack,
  Nav,
  validateFreeFormData,
  finalFreeRequest,
  setIsInternalLoading,
}) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const finalizeInFlightRef = useRef(false);

  useEffect(() => {
    setIsInternalLoading(isLoading);
  }, [isLoading, setIsInternalLoading]);

  const submit = async () => {
    if (finalizeInFlightRef.current) return;
    finalizeInFlightRef.current = true;
    setIsLoading(true);
    try {
      const validation = await validateFreeFormData();
      if (!validation.success) {
        dispatch(showNotification({ show: false, type: 'error', text: validation.message }));
        return;
      }
      await finalFreeRequest();
    } finally {
      finalizeInFlightRef.current = false;
      setIsLoading(false);
    }
  };

  return (
    <div data-cy="step7FreeReview">
      <Alert severity="success" sx={{ mb: 2 }}>
        Your {claim.quote.name} covers this {isFeatured ? 'Featured' : 'Standard'} listing. No payment or billing information is required.
      </Alert>
      <div className="cpg-order-recap">
        <span className="cpg-order-recap__label">{isFeatured ? 'Featured' : 'Standard'} 60-day listing</span>
        <span className="cpg-order-recap__price">$0</span>
      </div>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        ${claim.checkout.discountAmount} credit applied · Reserved until {formatBenefitDeadline(claim.checkout.expiresAt)}
      </Typography>
      <Nav
        nextDisabled
        onBack={onBack}
        textPreviousStep="← Back"
        nextButton={(
          <Button
            endIcon={(<ArrowRightIcon fontSize="small" />)}
            variant="contained"
            disabled={isLoading}
            type="button"
            onClick={submit}
            data-cy="completeFree"
          >
            Post job (no payment due)
          </Button>
        )}
      />
    </div>
  );
};

export const Step7 = memo(({
  Nav,
  onBack,
  defaultData,
  listingType,
  finalPaymentRequest,
  validateFormData,
  setIsInternalLoading,
  standardPrice,
  featuredPrice,
  clientSecret,
  intentError,
  retryIntent,
  benefitClaim,
  validateFreeFormData,
  finalFreeRequest,
}: IStep7Props) => {
  // Order-recap + pay CTA copy (visual-only; prices arrive from Step 1's query and are
  // undefined until it lands, so both the recap price line and the CTA guard for a finite number).
  const isFeaturedListing = listingType === 2;
  const recapPrice = benefitClaim?.checkout.finalAmount
    ?? (isFeaturedListing ? featuredPrice : standardPrice);
  const hasRecapPrice = typeof recapPrice === 'number' && Number.isFinite(recapPrice);
  const recapLabel = isFeaturedListing ? 'Featured 60-day listing' : 'Standard 60-day listing';
  const payCtaText = hasRecapPrice ? `Pay $${recapPrice}` : 'Process Purchase';

  if (benefitClaim?.checkout.finalAmount === 0 && validateFreeFormData && finalFreeRequest) {
    return (
      <CardWrapper classNameForInternalBlock="cpg-step-card">
        <PaymentTitle freeReview />
        <FreeReview
          claim={benefitClaim}
          isFeatured={isFeaturedListing}
          onBack={onBack}
          Nav={Nav}
          validateFreeFormData={validateFreeFormData}
          finalFreeRequest={finalFreeRequest}
          setIsInternalLoading={setIsInternalLoading}
        />
      </CardWrapper>
    );
  }

  return (
    <CardWrapper classNameForInternalBlock="cpg-step-card">
      <PaymentTitle />

      <div className="cpg-order-recap">
        <span className="cpg-order-recap__label">{recapLabel}</span>
        {hasRecapPrice && (
          <span className="cpg-order-recap__price">
            $
            {recapPrice}
          </span>
        )}
      </div>

      {benefitClaim && benefitClaim.checkout.finalAmount > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          ${benefitClaim.checkout.discountAmount} job credit applied. Pay the ${benefitClaim.checkout.finalAmount} balance by card or Link; bank account payment is unavailable for benefit checkouts.
        </Alert>
      )}

      <JobPaymentElements
        clientSecret={clientSecret}
        intentError={intentError}
        onRetry={retryIntent}
      >
        <Step7PaymentForm
          defaultData={defaultData}
          clientSecret={clientSecret}
          payCtaText={payCtaText}
          onBack={onBack}
          Nav={Nav}
          finalPaymentRequest={finalPaymentRequest}
          validateFormData={validateFormData}
          setIsInternalLoading={setIsInternalLoading}
        />
      </JobPaymentElements>
    </CardWrapper>
  );
});
